/**
 * Dev harness for FleetSparkline — renders all 6 visual states side-by-side.
 * Route: /dev/sparkline-harness (no auth, dev only)
 */

import { FleetSparkline } from '@/components/fleet/FleetSparkline';
import type { SparklinePoint, SparklineStatus } from '@/components/fleet/FleetSparkline';

function makeTestData(hours: number): SparklinePoint[] {
  return Array.from({ length: hours }, (_, i) => {
    const d = new Date();
    d.setHours(d.getHours() - (hours - 1 - i));
    const soc = i < 6 ? 80 - i * 5 : i < 18 ? 50 + (i - 6) * 4 : 98 - (i - 18) * 3;
    const pvKw = i >= 7 && i <= 17 ? Math.sin(((i - 7) / 10) * Math.PI) * 8 : 0;
    const loadKw = 1.5 + Math.sin(((i - 12) / 24) * Math.PI) * 0.8;
    return {
      bucket: d.toISOString(),
      avgSoc: Math.max(0, Math.min(100, soc)),
      avgPvKw: Math.max(0, pvKw),
      avgLoadKw: Math.max(0.5, loadKw),
    };
  });
}

const FULL_DATA = makeTestData(24);
const PARTIAL_DATA = makeTestData(6);

const STATES: { status: SparklineStatus; label: string; data: SparklinePoint[] }[] = [
  { status: 'empty', label: 'Empty (no data)', data: [] },
  { status: 'stale', label: 'Stale (50% opacity)', data: FULL_DATA },
  { status: 'healthy', label: 'Healthy', data: FULL_DATA },
  { status: 'warning', label: 'Warning (amber border)', data: FULL_DATA },
  { status: 'critical', label: 'Critical (red border)', data: FULL_DATA },
  { status: 'alert-active', label: 'Alert Active (red border)', data: PARTIAL_DATA },
];

export default function SparklineHarness() {
  return (
    <div className="min-h-screen bg-wt-bg-primary text-wt-text-primary p-8">
      <h1 className="text-xl font-semibold mb-6">FleetSparkline Dev Harness</h1>
      <p className="text-sm text-wt-text-secondary mb-8">
        All 6 visual states at 240px width (card default) and 120px width (compact).
      </p>

      <div className="space-y-8">
        {STATES.map(({ status, label, data }) => (
          <div key={status} className="space-y-2">
            <h2 className="text-sm font-medium text-wt-text-secondary">{label}</h2>
            <div className="flex items-start gap-6">
              <div className="space-y-1">
                <span className="text-[10px] text-wt-text-muted">240px</span>
                <FleetSparkline data={data} status={status} width={240} />
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-wt-text-muted">120px</span>
                <FleetSparkline data={data} status={status} width={120} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Render perf: 80 cards */}
      <div className="mt-12 border-t border-border pt-6">
        <h2 className="text-sm font-medium text-wt-text-secondary mb-4">
          Render perf: 80 sparklines (simulating fleet view)
        </h2>
        <RenderPerfTest />
      </div>
    </div>
  );
}

function RenderPerfTest() {
  const start = performance.now();
  const items = Array.from({ length: 80 }, (_, i) => (
    <FleetSparkline
      key={i}
      data={FULL_DATA}
      status={i % 5 === 0 ? 'warning' : i % 7 === 0 ? 'critical' : 'healthy'}
      width={240}
    />
  ));
  const elapsed = performance.now() - start;

  return (
    <>
      <p className="text-xs text-wt-text-muted mb-3">
        Render time for 80 sparklines: <span className="font-mono-numeric text-brand-accent">{elapsed.toFixed(1)}ms</span>
        {elapsed < 50 ? ' (PASS: <50ms)' : ' (FAIL: >50ms)'}
      </p>
      <div className="grid grid-cols-4 gap-2">
        {items}
      </div>
    </>
  );
}
